import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, ClipboardCheck, Loader2, Sparkles, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CreateQuiz() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [quizCount, setQuizCount] = useState(0);
  
  const [formData, setFormData] = useState({
    title: '',
    grade: '',
    subject: '',
    topic: '',
    num_questions: 5,
    question_types: ['multiple_choice', 'short_answer']
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, subRes, quizzesRes] = await Promise.all([
        axios.get(`${API_URL}/api/teacher/settings`),
        axios.get(`${API_URL}/api/subscription/status`),
        axios.get(`${API_URL}/api/assessments/quizzes`)
      ]);
      setSettings(settingsRes.data);
      setSubscription(subRes.data);
      setQuizCount(quizzesRes.data.length);
      
      if (settingsRes.data) {
        setFormData(prev => ({
          ...prev,
          grade: settingsRes.data.grades?.[0] || '',
          subject: settingsRes.data.subjects?.[0] || ''
        }));
      }
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  };

  const isTrial = subscription?.status === 'trial' || subscription?.subscription_tier?.startsWith('trial');
  const canCreateQuiz = !isTrial || quizCount < 1;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.grade) {
      toast.error('Please select a grade');
      return;
    }
    if (!formData.topic.trim()) {
      toast.error('Please enter a topic or content description');
      return;
    }

    // Auto-generate title if empty
    const title = formData.title.trim() || `${formData.grade} ${formData.subject} Quiz`;

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/assessments/quizzes/generate`, {
        ...formData,
        title
      });
      toast.success('Quiz generated successfully!');
      navigate(`/assessments/quiz/${response.data.quiz_id}`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Trial users can only create 1 quiz. Please subscribe for unlimited quizzes.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate quiz');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sky-50" data-testid="create-quiz-page">
      <header className="glass-header py-4 px-6">
        <div className="max-w-3xl mx-auto">
          <Button onClick={() => navigate('/assessments')} variant="ghost" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Assessments
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Trial Warning */}
        {isTrial && (
          <div className={`mb-6 p-4 rounded-xl border ${
            canCreateQuiz 
              ? 'bg-amber-50 border-amber-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 mt-0.5 ${canCreateQuiz ? 'text-amber-600' : 'text-red-600'}`} />
              <div>
                {canCreateQuiz ? (
                  <>
                    <p className="font-medium text-amber-800">Trial Account</p>
                    <p className="text-sm text-amber-700">
                      You can create 1 free quiz. Subscribe for unlimited quizzes.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-red-800">Quiz Limit Reached</p>
                    <p className="text-sm text-red-700">
                      Trial users can only create 1 quiz. <button onClick={() => navigate('/subscription')} className="underline font-medium">Subscribe now</button> for unlimited quizzes.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="glass-card p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-teal-500 rounded-2xl flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-slate-900">AI Quiz Generator</h2>
              <p className="text-slate-600">Just enter grade and topic - AI does the rest!</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Grade Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Grade <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7'].map(grade => (
                  <button
                    key={grade}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, grade }))}
                    className={`px-4 py-2 rounded-full font-medium transition-all ${
                      formData.grade === grade
                        ? 'bg-teal-600 text-white'
                        : 'bg-white border border-slate-200 text-slate-700 hover:border-teal-500'
                    }`}
                    data-testid={`grade-${grade.replace(' ', '-')}`}
                  >
                    {grade}
                  </button>
                ))}
              </div>
            </div>

            {/* Subject Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Subject (Optional)</label>
              <select
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="subject-select"
              >
                <option value="">Any Subject</option>
                <option value="Mathematics">Mathematics</option>
                <option value="English">English</option>
                <option value="Afrikaans">Afrikaans</option>
                <option value="Life Skills">Life Skills</option>
                <option value="Natural Sciences">Natural Sciences</option>
                <option value="Social Sciences">Social Sciences</option>
                <option value="Technology">Technology</option>
              </select>
            </div>

            {/* Topic - Main Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Topic / Content Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.topic}
                onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                placeholder="Enter your topic or paste content here. You can write a paragraph describing what you want questions about.

Example: 'Fractions - adding and subtracting fractions with different denominators. Students should understand equivalent fractions and how to find common denominators.'

Or simply: 'Addition and subtraction of 2-digit numbers'"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 min-h-[150px] resize-y"
                data-testid="topic-input"
              />
              <p className="text-sm text-slate-500 mt-2">
                The more detail you provide, the better the AI will generate questions.
              </p>
            </div>

            {/* Number of Questions */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Number of Questions</label>
              <div className="flex gap-2">
                {[3, 5, 7, 10].map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, num_questions: num }))}
                    className={`px-5 py-2 rounded-full font-medium transition-all ${
                      formData.num_questions === num
                        ? 'bg-teal-600 text-white'
                        : 'bg-white border border-slate-200 text-slate-700 hover:border-teal-500'
                    }`}
                    data-testid={`num-${num}`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {/* Optional Title */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Quiz Title (Optional)</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Leave empty for auto-generated title"
                className="w-full"
                data-testid="title-input"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading || !canCreateQuiz}
                className="w-full bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white rounded-full py-6 text-lg font-semibold shadow-lg"
                data-testid="generate-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Quiz with AI...
                  </>
                ) : !canCreateQuiz ? (
                  'Quiz Limit Reached - Subscribe to Continue'
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Quiz with AI
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
